import {
	Box,
	Center,
	Divider,
	Group,
	Menu,
	Modal,
	Select,
	Stack,
	useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type { UserOut as IUser } from '@repo/api-codegen/v2';
import {
	Badge,
	Banner,
	Button,
	IconButton,
	Text,
	Title,
} from '@repo/foundations';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useApiImpersonateUser, useApiListIamRoles } from '@repo/api-codegen';
import { SettingsButton } from '@repo/common/components';
import { UserRole } from '@repo/common/enums/UserRole';
import { titleCase } from '@repo/common/utils';
import type {
	DataTableColumn,
	DataTableSortStatus,
} from '@repo/mantine-datatable';
import { DataTable } from '@repo/mantine-datatable';
import Papa from 'papaparse';
import {
	queryClient,
	useAuthUser,
	useInviteUsers,
	useUpdateUser,
	usersQueryKeyFactory,
} from '../../../api';
import { useExtendedUserList } from '../../../api/hooks/user/useExtendedUserList';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';
import {
	clearDexieAndQueryClient,
	clearTokens,
	isImpersonating,
} from '../../../utils/cache/utils';
import { DateRenderer } from '../../CatalogView/TableView/DateRenderer';
import { Guard } from '../../Guard/Guard';
import { closeModal, openModal } from '../../ModalManager';
import { InviteMembersModal } from '../../PriceIncreaseModals';
import SearchBox from '../../SearchBox/SearchBox';
import { ROW_HEIGHT, useTableStyles } from '../../TableV2/TableV2.styles';
import { useTableV2Height } from '../../TableV2/useTableV2Height';
import { UserAvatar } from '../../UserAvatar';
import { SettingsTableEmpty } from '../Empty';
import { IamRoleSelector } from '../Selectors/IamRoleSelector';
import RoleSelect from '../Selectors/LegacyRoleSelector';
import { useMembersTabSelectRole } from '../Selectors/LegacyRoleSelector.helpers';

type MembersListFilterType =
	| 'Members'
	| 'INVITED'
	| 'REMOVED'
	| keyof typeof UserRole;

interface RemoveMemberModalProps {
	user: IUser;
	onClose: VoidFunction;
	onConfirm: (user: IUser) => Promise<void>;
}

const sortByLastLogin = (a: IUser, b: IUser) => {
	if (!a.last_login) return 1;
	if (!b.last_login) return -1;
	return new Date(a.last_login).getTime() - new Date(b.last_login).getTime();
};

const sortByRole = (a: IUser, b: IUser) => {
	// This is the order of roles, that is always used.
	const roleOrder = ['ADMIN', 'EDITOR', 'VIEWER'];

	const roleA = a.role?.toUpperCase();
	const roleB = b.role?.toUpperCase();

	// Determine the role index for each user.
	const roleIndexA =
		roleOrder.indexOf(roleA) !== -1
			? roleOrder.indexOf(roleA)
			: roleOrder.length;

	const roleIndexB =
		roleOrder.indexOf(roleB) !== -1
			? roleOrder.indexOf(roleB)
			: roleOrder.length;

	// Compare roles based on role index
	if (roleIndexA < roleIndexB) return -1;
	if (roleIndexA > roleIndexB) return 1;

	// If roles are the same, compare display names.
	return a.display_name.localeCompare(b.display_name);
};

export function Members() {
	const theme = useMantineTheme();
	const [queryParams, setQueryParams] = useSearchParams();
	const { user: currentUser, workspace } = useAuthUser();
	const { activeUsers, disabledUsers, pendingUsers } = useExtendedUserList();
	const { hasPermission: canEditUsers } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Users.Update',
		defaultResult: false,
	});

	const { hasPermission: canRemoveUsers } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Users.Delete',
		defaultResult: false,
	});

	const navigate = useNavigate();

	const [searchTerm, setSearch] = useState('');
	const [selectedFilter, setSelectedFilter] = useState<MembersListFilterType>(
		(queryParams.get('member_role') as MembersListFilterType) ?? 'Members'
	);

	const [opened, { open, close }] = useDisclosure(false);

	const { selectRole } = useMembersTabSelectRole();
	const { mutateAsync: inviteUsers } = useInviteUsers(
		usersQueryKeyFactory.list()
	);
	const { mutateAsync: updateUser } = useUpdateUser({
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: usersQueryKeyFactory.allLists(),
				});
			},
		},
	});

	const [userToRemove, setUserToRemove] = useState<IUser | null>(null);
	const [sortStatus, setSortStatus] = useState<DataTableSortStatus>({
		columnAccessor: 'role',
		direction: 'asc',
	});

	const {
		mutateAsync: impersonateUser,
		isPending: isSendingImpersonatingRequest,
	} = useApiImpersonateUser();

	const handleToggleActivation = useCallback(
		async (toggleUser: IUser) => {
			await updateUser({
				data: {
					id: toggleUser.id,
					disabled: !toggleUser.disabled,
				},
			});
			showNotification({
				message: !toggleUser.disabled
					? `${toggleUser.display_name} has been removed from your workspace`
					: 'User activated',
			});
			setUserToRemove(null);
		},
		[updateUser]
	);

	const handleInviteMembers = async ({
		emails,
		role,
		iam_role_id,
		groups,
		teams,
	}: {
		emails: string[];
		role: UserRole;
		iam_role_id?: string | null;
		groups: string[];
		teams: string[];
	}) => {
		await inviteUsers({
			emails,
			role,
			groups,
			teams,
			iam_role_id: iam_role_id,
		});
	};

	const handleSelectedFilterChange = useCallback(
		(value: MembersListFilterType) => {
			setQueryParams((prev) => ({ ...prev, member_role: value }));
			setSelectedFilter(value);
		},
		[setQueryParams]
	);

	const users = useMemo(() => {
		const initialUsers = [
			...(activeUsers ?? []),
			...(disabledUsers ?? []),
			...(pendingUsers ?? []),
		];

		let filtered = [];

		if (selectedFilter === 'Members') {
			filtered = initialUsers.filter((user) => !user.pending && !user.disabled);
		} else if (selectedFilter === 'INVITED') {
			filtered = initialUsers.filter((user) => user.pending && !user.disabled);
		} else if (selectedFilter === 'REMOVED') {
			filtered = initialUsers.filter((user) => user.disabled);
		} else {
			filtered = initialUsers.filter(
				(user) =>
					user.role &&
					user.role.toLowerCase() === selectedFilter?.toLowerCase() &&
					!user.disabled &&
					!user.pending
			);
		}

		const sorted = filtered
			.filter(
				(user) =>
					user.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
					user.email.toLowerCase().includes(searchTerm.toLowerCase())
			)
			.sort(
				sortStatus.columnAccessor === 'last_login'
					? sortByLastLogin
					: sortByRole
			);

		if (sortStatus.direction === 'desc') {
			return sorted.reverse();
		}
		return sorted;
	}, [
		activeUsers,
		disabledUsers,
		pendingUsers,
		searchTerm,
		selectedFilter,
		sortStatus,
	]);

	const handleOpenUser = useCallback(
		(userId: string) => {
			navigate(`/user/${userId}`);
		},
		[navigate]
	);

	const handleCsvExport = () => {
		const data = users.map((user) => ({
			'Display Name': user.display_name,
			Email: user.email,
			Role: user.role,
		}));

		const csvContent = Papa.unparse(data, {
			escapeFormulae: true,
		});

		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.setAttribute('download', `Secoda ${workspace.name} members.csv`);
		link.click();
	};

	const handleRemoveClick = useCallback((user: IUser) => {
		setUserToRemove(user);
	}, []);

	const handleRemoveClose = useCallback(() => {
		setUserToRemove(null);
	}, []);

	const handleImpersonate = useCallback(
		async (user: IUser) => {
			try {
				await impersonateUser({
					body: {
						user_id: user.id,
					},
				});
				clearTokens();
				await clearDexieAndQueryClient();
				window.location.replace('/');
			} catch (error) {
				showNotification({
					message: 'Failed to impersonate user',
					color: 'red',
				});
			}
		},
		[impersonateUser]
	);

	const handleImpersonateClick = useCallback(
		(user: IUser) => {
			openModal({
				modalId: 'impersonate-modal',
				title: `Impersonate ${user.display_name}?`,
				children: (
					<Stack>
						<Text size="sm">
							By impersonating {user.display_name}, you will be able to view and
							make changes as if you were the user.
						</Text>
						<Divider />
						<Group position="right">
							<Button
								variant="default"
								tone="default"
								onClick={() => closeModal('impersonate-modal')}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								loading={isSendingImpersonatingRequest}
								onClick={async () => {
									await handleImpersonate(user);
									closeModal('impersonate-modal');
								}}
							>
								Impersonate
							</Button>
						</Group>
					</Stack>
				),
			});
		},
		[handleImpersonate, isSendingImpersonatingRequest]
	);

	const { data: iamRoles } = useApiListIamRoles({});
	const roleFilterOptions = [
		'Members',
		'ADMIN',
		'EDITOR',
		'VIEWER',
		...(workspace.migrated_permission_v2 ? ['CUSTOM'] : []),
		'INVITED',
		'REMOVED',
	];

	const columns = useMemo<DataTableColumn<IUser>[]>(
		() => [
			{
				title: (
					<Text size="sm" fw={500} color="text/secondary/default">
						Name
					</Text>
				),
				accessor: 'display_name',
				width: 300,
				render: (user) => (
					<Group>
						<UserAvatar user={user} size="sm" />
						<Stack maw={340} mah={`${ROW_HEIGHT}px`} spacing={0}>
							<Title size="sm">{user.display_name}</Title>
							<Text size="xs" color="text/secondary/default" lineClamp={1}>
								{user.email}
							</Text>
						</Stack>

						{(user.disabled || user.pending) &&
							(user.pending && !user.disabled ? (
								<Center>
									<Badge>Invited</Badge>
								</Center>
							) : (
								<Center>
									<Badge>Removed</Badge>
								</Center>
							))}
					</Group>
				),
			},
			{
				title: (
					<Text size="sm" fw={500} color="text/secondary/default">
						Last login
					</Text>
				),
				accessor: 'last_login',
				width: 300,
				sortable: true,
				render: (user) =>
					user.last_login && (
						<DateRenderer value={`${user.last_login}`} size="sm" />
					),
			},
			{
				title: (
					<Text size="sm" fw={500} color="text/secondary/default">
						Role
					</Text>
				),
				accessor: 'role',
				sortable: true,
				render: (user) => {
					const disabled = !canEditUsers || currentUser?.id === user.id;
					return (
						<Box
							onClick={(e) => {
								if (!disabled) e.stopPropagation();
							}}
						>
							{!user.disabled &&
								(workspace.migrated_permission_v2 ? (
									<IamRoleSelector disabled={disabled} user={user} />
								) : (
									<RoleSelect
										disabled={disabled}
										role={user.role as UserRole}
										selectRole={(role) => selectRole(user, role)}
										legacyStyle={false}
									/>
								))}
						</Box>
					);
				},
			},
			{
				title: '',
				accessor: 'actions',
				textAlignment: 'right',
				render: (user) => {
					const hasMenuItems =
						!workspace.enforce_scim_only_provisioning ||
						(currentUser?.id !== user.id && !isImpersonating());

					if (!hasMenuItems) {
						return null;
					}

					return (
						<Guard
							v1AllowedRoles={[UserRole.ADMIN]}
							v2Permission="Users.Delete"
						>
							<Menu position="bottom-end">
								<Menu.Target>
									<IconButton
										onClick={(e) => e.stopPropagation()}
										iconName="dots"
										variant="tertiary"
										aria-label="row-actions"
									/>
								</Menu.Target>
								<Menu.Dropdown>
									{!workspace.enforce_scim_only_provisioning && (
										<Menu.Item
											onClick={(e) => {
												e.stopPropagation();

												user.disabled
													? handleToggleActivation(user)
													: handleRemoveClick(user);
											}}
										>
											{user.disabled ? 'Activate' : 'Remove'}
										</Menu.Item>
									)}
									{currentUser?.id !== user.id && !isImpersonating() && (
										<Menu.Item
											onClick={(e) => {
												e.stopPropagation();
												handleImpersonateClick(user);
											}}
										>
											Impersonate
										</Menu.Item>
									)}
								</Menu.Dropdown>
							</Menu>
						</Guard>
					);
				},
			},
		],
		[
			canEditUsers,
			currentUser?.id,
			workspace.migrated_permission_v2,
			workspace.enforce_scim_only_provisioning,
			selectRole,
			handleToggleActivation,
			handleRemoveClick,
			handleImpersonateClick,
		]
	);

	const { classes: tableClasses } = useTableStyles({
		hideCheckbox: true,
		withStickyColumnBorder: false,
		withHover: false,
	});

	const { tableRef, tableHeight } = useTableV2Height({
		totalCountWithNesting: users.length,
		paginationSize: 50,
		height: undefined,
		extraFooterSpace: false,
	});
	return (
		<Stack spacing={'xl'}>
			{!workspace?.enforce_scim_only_provisioning ? (
				<Guard v1AllowedRoles={[UserRole.ADMIN]} v2Permission="Users.Create">
					<Box mt={'xl'}>
						<SettingsButton
							title="Add members"
							description="Members can view and edit your workspace depending on their assigned roles"
							onClick={open}
							buttonText="Invite members"
						/>
					</Box>
				</Guard>
			) : (
				<Box mt="xl">
					<Banner
						tone="neutral"
						message="Invitations are disabled for this workspace. Users must be managed via SCIM"
					/>
				</Box>
			)}
			<Title order={3} weight="semibold" size="md">
				<Group noWrap>
					<SearchBox placeholder="Search by name" onSearch={setSearch} />
					<Select
						data={roleFilterOptions.map((value) => ({
							label: titleCase(value),
							value,
						}))}
						value={selectedFilter}
						onChange={handleSelectedFilterChange}
					/>
					<IconButton
						tooltip="Export CSV file"
						iconName="download"
						onClick={handleCsvExport}
					/>
				</Group>
			</Title>

			{users?.length === 0 && (
				<SettingsTableEmpty
					title="No users found."
					description="Add a user to get started."
				/>
			)}

			{users?.length > 0 && (
				<Box h="100%" ref={tableRef}>
					<DataTable
						columns={columns}
						records={users}
						height={tableHeight}
						onRowClick={(user) => handleOpenUser(user.id)}
						withBorder
						borderRadius="md"
						classNames={tableClasses}
						withStickyColumnBorder={false}
						sortStatus={sortStatus}
						onSortStatusChange={setSortStatus}
					/>
				</Box>
			)}

			{userToRemove && (
				<RemoveMemberModal
					user={userToRemove}
					onClose={handleRemoveClose}
					onConfirm={handleToggleActivation}
				/>
			)}

			<InviteMembersModal
				opened={opened}
				onClose={close}
				onConfirm={handleInviteMembers}
			/>
		</Stack>
	);
}

function RemoveMemberModal({
	user,
	onClose,
	onConfirm,
}: RemoveMemberModalProps) {
	return (
		<Modal
			opened
			size="md"
			title={`Remove ${user.display_name}?`}
			onClose={onClose}
		>
			<Stack>
				<Text size="sm">
					Removing this user will revoke all their access to the workspace.
				</Text>
				<Divider />
				<Group position="right">
					<Button variant="default" tone="default" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="primary"
						tone="critical"
						onClick={() => onConfirm(user)}
					>
						Remove user
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
